import type { Node } from "prosemirror-model";
import { ReplaceStep, type Step } from "prosemirror-transform";

/**
 * Creates minimal `ReplaceStep`s to transform oldDoc into newDoc.
 *
 * As of writing, this function generates very coarse-grained, sequential, block-level
 * `ReplaceStep`s. We use ProseMirror's native `findDiffStart` and `findDiffEnd` functions to try to
 * make the `ReplaceStep`s slightly more precise, but this is not for correctness, it's so that if a
 * "foreign" edit changes only one part of a paragraph, we see only that change, rather than a block
 * replacement.
 *
 * The goal of this change specifically is to work well with `prosemirror-suggest-changes`'s
 * `transformToSuggestionTransaction` function. Note that `recreateTransform` generally does not,
 * for many reasons: both word- and character-level diffs often result in suggestions that are not
 * helpful to humans, it takes work to avoid spurious suggestions (attr changes often produce nested
 * suggestion marks), and so on.
 *
 * Key behaviors:
 *
 *   - For inline text changes within the same block type, creates a step that uses `findDiffStart`
 *     and `findDiffEnd` to find a better-than-nothing-but-not-perfect diff region.
 *   - For block type changes (e.g., paragraph to heading), replaces the entire block (produces
 *     block-level suggestion marks).
 *   - For changes affecting multiple blocks, creates independent steps for each.
 */
export function createDiffSteps(oldDoc: Node, newDoc: Node): Step[] {
	if (oldDoc.eq(newDoc)) {
		return [];
	}

	// Try pairwise block-level diffing first.
	const blockSteps = createBlockLevelSteps(oldDoc, newDoc);
	if (blockSteps.length > 0) {
		return blockSteps;
	}

	// Fall back to a single document-level step
	const singleStep = createSingleReplaceStep(oldDoc, newDoc);
	return singleStep ? [singleStep] : [];
}

/**
 * Creates independent ReplaceSteps for each top-level block that changed.
 *
 * For blocks of the same type with only content changes, creates a precise step that replaces only
 * the differing content (inline suggestion marks). For block type changes, replaces the entire
 * block (block-level marks).
 */
function createBlockLevelSteps(oldDoc: Node, newDoc: Node): Step[] {
	const steps: Step[] = [];

	// Compare top-level blocks and create steps for changed ones.
	const oldBlocks = getTopLevelBlocks(oldDoc);
	const newBlocks = getTopLevelBlocks(newDoc);

	// Handle simple case: same number of blocks, check each for changes.
	if (oldBlocks.length === newBlocks.length) {
		// Track cumulative offset: as each step is applied, positions shift by the difference
		// between new content size and old content size.
		let cumulativeOffset = 0;

		for (let i = 0; i < oldBlocks.length; i++) {
			const oldBlock = oldBlocks[i];
			const newBlock = newBlocks[i];

			if (!oldBlock.node.eq(newBlock.node)) {
				const step = createStepForBlockChange(oldBlock, newDoc, newBlock, cumulativeOffset);
				if (step) {
					steps.push(step);
					// Update offset: next steps need to account for size change
					cumulativeOffset += newBlock.node.nodeSize - oldBlock.node.nodeSize;
				}
			}
		}
		return steps;
	}

	// For different block counts, we can't easily create independent steps without complex
	// alignment logic, so fall back to single step.
	return [];
}

/**
 * Creates a step for a changed block.
 *
 * If the block type is the same and only content changed, creates a precise step for the inline
 * diff. Otherwise, replaces the entire block.
 */
function createStepForBlockChange(
	oldBlock: BlockInfo,
	newDoc: Node,
	newBlock: BlockInfo,
	positionOffset: number
): Step | null {
	const oldNode = oldBlock.node;
	const newNode = newBlock.node;

	// Adjust positions for steps that have already been applied.
	const adjustedOldPos = oldBlock.pos + positionOffset;

	// If block type or markup differs, replace the whole block. This produces block-level
	// `<del>`/`<ins>` marks, which is appropriate for structural changes like transforming a
	// paragraph into a heading.
	if (!oldNode.sameMarkup(newNode)) {
		return new ReplaceStep(
			adjustedOldPos,
			adjustedOldPos + oldNode.nodeSize,
			newDoc.slice(newBlock.pos, newBlock.pos + newNode.nodeSize)
		);
	}

	// Same block type. Diff the content to find precise change region. This produces inline
	// suggestion marks for text changes.
	const contentDiffStart = oldNode.content.findDiffStart(newNode.content);
	if (contentDiffStart === null) {
		// Content is identical. TECHNICALLY, this shouldn't happen since we checked equality above?
		return null;
	}

	const contentDiffEnd = oldNode.content.findDiffEnd(newNode.content);
	if (!contentDiffEnd) {
		return null;
	}

	let { a: endOld, b: endNew } = contentDiffEnd;

	// `findDiffEnd` can produce indexes that occur before `findDiffStart`, and to generate a valid
	// `ReplaceStep`, we need to account for this. This code is similar to the code found in
	// ProseMirror's own `Transform.replace`, which handles this specific case.
	//
	// Consider these two documents:
	//
	//   - Old: "AA"
	//   - New: "A"
	//
	// What we want is:
	//
	//   - Old: replace [1, 2) with nothing (delete the second "A")
	//   - New: replace [1, 1) with nothing (insert nothing)
	//
	// But, `findDiffStart` produces 1, and `findDiffEnd` produces `endOld = 1` and `endNew = 0`.
	// Since `endNew` is smaller than `diffStart`, we need to adjust them to account for this
	// overlap.
	const overlap = contentDiffStart - Math.min(endOld, endNew);
	if (overlap > 0) {
		endOld += overlap;
		endNew += overlap;
	}

	// Convert content positions to document positions. Adjusted block position + 1 (to enter the
	// block) + content offset.
	const docStart = adjustedOldPos + 1 + contentDiffStart;
	const docEndOld = adjustedOldPos + 1 + endOld;
	const newDocStart = newBlock.pos + 1 + contentDiffStart;
	const newDocEnd = newBlock.pos + 1 + endNew;

	// Create a step that replaces just the differing content
	return new ReplaceStep(docStart, docEndOld, newDoc.slice(newDocStart, newDocEnd));
}

/**
 * Creates a single ReplaceStep that transforms oldDoc into newDoc.
 *
 * Uses ProseMirror's native `findDiffStart`/`findDiffEnd` to compute the minimal "diff region",
 * ensuring the step represents exactly what changed.
 */
function createSingleReplaceStep(oldDoc: Node, newDoc: Node): Step | null {
	// Find where the documents first differ.
	const diffStart = oldDoc.content.findDiffStart(newDoc.content);

	if (diffStart === null) {
		// Documents are identical.
		return null;
	}

	// Find where they stop differing (from the end).
	const diffEnd = oldDoc.content.findDiffEnd(newDoc.content);

	if (!diffEnd) {
		// This shouldn't happen if `diffStart` is non-null, but whatever, not destructive, just
		// return null.
		return null;
	}

	let { a: endOld, b: endNew } = diffEnd;

	// `findDiffEnd` can produce indexes that occur before `findDiffStart`, and to generate a valid
	// `ReplaceStep`, we need to account for this. This code is similar to the code found in
	// ProseMirror's own `Transform.replace`, which handles this specific case.
	//
	// Consider these two documents:
	//
	//   - Old: "AA"
	//   - New: "A"
	//
	// What we want is:
	//
	//   - Old: replace [1, 2) with nothing (delete the second "A")
	//   - New: replace [1, 1) with nothing (insert nothing)
	//
	// But, `findDiffStart` produces 1, and `findDiffEnd` produces `endOld = 1` and `endNew = 0`.
	// Since `endNew` is smaller than `diffStart`, we need to adjust them to account for this
	// overlap.

	const overlap = diffStart - Math.min(endOld, endNew);
	if (overlap > 0) {
		endOld += overlap;
		endNew += overlap;
	}

	// Create the slice from the new document.
	const slice = newDoc.slice(diffStart, endNew);

	return new ReplaceStep(diffStart, endOld, slice);
}

interface BlockInfo {
	pos: number;
	node: Node;
}

function getTopLevelBlocks(doc: Node): BlockInfo[] {
	const blocks: BlockInfo[] = [];
	let pos = 0;

	doc.forEach((node) => {
		blocks.push({ pos, node });
		pos += node.nodeSize;
	});

	return blocks;
}
